import { AlertTriangle, Check } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';

export type ConnectRecoveryReason =
  | 'popup_closed_without_result'
  | 'popup_blocked'
  | 'status_not_confirmed';

export type ConnectRecoveryCause = 'admin' | 'number_in_use' | 'other_provider' | 'blocker';

// Clickable because Meta rejects most attempts without ever redirecting back, which makes the
// user's own answer the only first-party record of which of these four actually happened.
const CAUSES: readonly { id: ConnectRecoveryCause; key: string }[] = [
  { id: 'admin', key: 'whatsapp_phone_numbers.setup.connect_recovery_cause_admin' },
  {
    id: 'number_in_use',
    key: 'whatsapp_phone_numbers.setup.connect_recovery_cause_number_in_use',
  },
  {
    id: 'other_provider',
    key: 'whatsapp_phone_numbers.setup.connect_recovery_cause_other_provider',
  },
  { id: 'blocker', key: 'whatsapp_phone_numbers.setup.connect_recovery_cause_blocker' },
] as const;

const BOLD_LEAD = <strong className="font-semibold" />;

interface ConnectRecoveryPanelProps {
  reason: ConnectRecoveryReason;
  onRetry: () => void;
  onContinueInTab: () => void;
  onDismiss: () => void;
  onReportCause: (cause: ConnectRecoveryCause) => void;
  retryDisabled: boolean;
  continueDisabled: boolean;
  docsHref: string;
}

export const ConnectRecoveryPanel = ({
  reason,
  onRetry,
  onContinueInTab,
  onDismiss,
  onReportCause,
  retryDisabled,
  continueDisabled,
  docsHref,
}: ConnectRecoveryPanelProps) => {
  const { t } = useTranslation(['integrations']);
  const blocked = reason === 'popup_blocked';
  const [reportedCause, setReportedCause] = useState<ConnectRecoveryCause | null>(null);

  const reportCause = (cause: ConnectRecoveryCause) => {
    if (reportedCause) return;
    setReportedCause(cause);
    onReportCause(cause);
  };

  return (
    <Alert variant="warning" data-testid="whatsapp-connect-recovery-panel">
      <AlertTriangle className="size-4" />
      <AlertTitle>
        {t(
          blocked
            ? 'whatsapp_phone_numbers.setup.connect_blocked_title'
            : 'whatsapp_phone_numbers.setup.connect_recovery_title',
        )}
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <p className="text-xs">
          {t(
            blocked
              ? 'whatsapp_phone_numbers.setup.connect_blocked_lead'
              : 'whatsapp_phone_numbers.setup.connect_recovery_lead',
          )}
        </p>
        {!blocked && (
          <>
            <p className="text-xs font-medium">
              {t('whatsapp_phone_numbers.setup.connect_recovery_cause_prompt')}
            </p>
            {/* Answering locks the list but never dims it: these four lines are also the
                remediation steps, and 12px text at reduced opacity fails AA on warning. */}
            <ul className="flex flex-col gap-1">
              {CAUSES.map(({ id, key }) => {
                const selected = reportedCause === id;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => reportCause(id)}
                      disabled={reportedCause !== null}
                      aria-pressed={selected}
                      className="hover:bg-warning-muted flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors disabled:cursor-default disabled:hover:bg-transparent"
                      data-testid={`whatsapp-connect-recovery-cause-${id}`}
                    >
                      <Check
                        className={`mt-0.5 size-3.5 shrink-0 ${selected ? 'opacity-100' : 'opacity-0'}`}
                        aria-hidden
                      />
                      <span>
                        <Trans t={t} i18nKey={key} components={[BOLD_LEAD]} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {reportedCause && (
              <p className="text-xs" data-testid="whatsapp-connect-recovery-cause-thanks">
                {t('whatsapp_phone_numbers.setup.connect_recovery_cause_thanks')}
              </p>
            )}
          </>
        )}
      </AlertDescription>
      <div className="flex flex-col gap-2 pt-3 !pl-0">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={onRetry}
            size="sm"
            variant="dark"
            disabled={retryDisabled}
            data-testid="whatsapp-connect-recovery-retry"
          >
            {t('whatsapp_phone_numbers.setup.connect_recovery_retry_button')}
          </Button>
          <Button
            onClick={onContinueInTab}
            size="sm"
            variant="secondary"
            disabled={continueDisabled}
            data-testid="whatsapp-connect-recovery-continue-in-tab"
          >
            {t('whatsapp_phone_numbers.setup.connect_recovery_in_tab_button')}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            asChild
            size="sm"
            variant="link"
            className="text-warning-foreground px-0"
            data-testid="whatsapp-connect-recovery-docs"
          >
            <a href={docsHref} target="_blank" rel="noopener noreferrer">
              {t('whatsapp_phone_numbers.setup.connect_recovery_docs_link')}
            </a>
          </Button>
          <Button
            asChild
            size="sm"
            variant="link"
            className="text-warning-foreground px-0"
            data-testid="whatsapp-connect-recovery-support"
          >
            <a href="mailto:support@quickchat.ai">
              {t('whatsapp_phone_numbers.setup.connect_recovery_support_link')}
            </a>
          </Button>
          {/* Never disabled: the only guaranteed way back to the normal sheet. */}
          <Button
            onClick={onDismiss}
            size="sm"
            variant="link"
            className="text-warning-foreground px-0"
            data-testid="whatsapp-connect-recovery-dismiss"
          >
            {t('whatsapp_phone_numbers.setup.connect_recovery_dismiss_button')}
          </Button>
        </div>
      </div>
    </Alert>
  );
};
